/**
 * Sidebar navigation source-of-truth (`useNavItems`).
 *
 * Returns the flat list of labeled groups that backs the sidebar shell in
 * `src/layouts/components/navigation`.
 */
import {
  AdminPanelSettingsOutlined,
  AttachMoneyOutlined,
  CodeOutlined,
  InsightsOutlined,
  MonitorHeartOutlined,
  MoreHorizOutlined,
  WidgetsOutlined
} from '@mui/icons-material'
import { compact } from 'lodash'
import { useLocale, useTranslations } from 'next-intl'
import { useMemo } from 'react'
import type { NavGroup, NavItems, NavLink } from 'src/@core/layouts/types'

import getCustomerAssets from '~/auth/assets'
import useIsReadOnly from '~/auth/hooks/useIsReadOnly'
import { useGateValue, useStatsigGate } from '~/configs/statsig'
import { useCurrentOrg, useOperationsEnabled, useWorkflowsEnabled } from '~/data/api/hooks/organizations'
import { getSynopDesktop } from '~/features/desktop/bridge'

import routeDirectory from '../routes'

const {
  batteries,
  chargeai,
  chargers,
  dashboard,
  generativeDashboard,
  meters,
  operations,
  reports,
  settings,
  sites,
  vehicles,
  workflows
} = routeDirectory

const adminOnly = {
  action: 'manage',
  subject: 'admin'
} as const

const sortByTitle = (items: NavLink[], locale: string) =>
  [...items].sort((a, b) => a.title.localeCompare(b.title, locale))

const OPERATIONS_PROJECT_TASK_PATH = new RegExp(`^${operations.root}projects/[^/]+/tasks/[^/]+`)
const OPERATIONS_PROJECT_DETAIL_PATH = new RegExp(`^${operations.root}projects/[^/]+/?$`)

export const isOperationsTicketDetailPath = (pathname: string) =>
  pathname.startsWith(`${operations.root}tickets/`) || OPERATIONS_PROJECT_TASK_PATH.test(pathname)

export const isOperationsProjectDetailPath = (pathname: string) => OPERATIONS_PROJECT_DETAIL_PATH.test(pathname)

export const isOperationsPlanDetailPath = (pathname: string) =>
  pathname.startsWith(`${operations.root}plans/`) && pathname !== `${operations.root}plans/`

export default function useNavItems(): NavItems {
  const t = useTranslations('layouts.navDrawer')
  const locale = useLocale()
  const { org: currentOrganization } = useCurrentOrg()

  // The billing backend has no read-only access level, so read-only users are denied the entire
  // billing section (route guard in `src/app/settings/(billing)/layout.tsx`); hide its nav entries too.
  const readOnly = useIsReadOnly()

  // SynopLinks is not feature-gated: every organization can open the list,
  // including orgs with zero Links (empty-state messaging lives on the page).
  const isSynopInternal = useGateValue('synop_internal_only')
  const hasBatteries = useGateValue('batteries-in-ui')
  const hasPanels = useGateValue('panels-in-ui')
  const hasFuelTanks = useGateValue('fuel-tanks-in-ui')
  const hasGridConnections = useGateValue('grid-connections-in-ui')
  const hasLoads = useGateValue('loads-in-ui')
  const hasMeters = useGateValue('meters-in-the-ui')
  const hasSolarArrays = useGateValue('solar-in-ui')
  const hasIntegrations = useGateValue('integrations')
  const {
    hasCostExplorer,
    hasSynopAutopilot,
    reimbursementsStatus,
    fleetBillingStatus,
    workplaceChargingStatus,
    publicChargingStatus,
    canEditTheme,
    hasProductsAndAgreements
  } = currentOrganization ?? {}

  const { enabled: hasInvoiceTemplates } = useStatsigGate('invoice-templates')
  const { enabled: hasTaxManagement } = useStatsigGate('tax-management')
  const { enabled: hasAssetSharing } = useStatsigGate('asset-sharing')
  const { enabled: hasWorkflows } = useWorkflowsEnabled()
  const { enabled: hasOperations } = useOperationsEnabled()
  const { enabled: operationsSettings } = useStatsigGate('operations-ui')
  const { enabled: aiAssistant } = useStatsigGate('ai-assistant')
  const { enabled: assistantUsage } = useStatsigGate('assistant-usage')
  const { enabled: reportHub } = useStatsigGate('report_hub')
  const { enabled: generativeDashboardEnabled } = useStatsigGate('generative-dashboard')
  const { enabled: hasAdvancedFirmware } = useStatsigGate('advanced-firmware')
  const { enabled: hasDesktopApp } = useStatsigGate('desktop-app')
  const showProductsAndAgreements = (hasProductsAndAgreements || fleetBillingStatus === 'ENABLED') ?? false
  const showHomeCharging = ['ENABLED', 'DISABLED'].includes(reimbursementsStatus ?? '')
  const showWorkplaceCharging = ['ENABLED', 'DISABLED'].includes(workplaceChargingStatus ?? '')
  const showPublicCharging = ['ENABLED', 'DISABLED'].includes(publicChargingStatus ?? '')
  const showFleetBilling = ['ENABLED', 'DISABLED'].includes(fleetBillingStatus ?? '')

  // Inside the desktop shell the download link would only offer what is
  // already running. The bridge is injected before any script runs, so one
  // read at mount is enough.
  const isDesktopShell = useMemo(() => getSynopDesktop() !== undefined, [])

  return useMemo<NavItems>(() => {
    const monitoring: NavGroup = {
      key: 'monitoring',
      title: t('monitoring'),
      icon: <MonitorHeartOutlined />,
      defaultOpen: true,
      // Notifications intentionally omitted — same activity is surfaced by
      // the bell icon in the top bar, so duplicating it here is just noise.
      items: [
        { title: t('dashboard'), path: dashboard.root },
        { title: t('sites'), path: sites.root }
      ]
    }

    // Assets and Intelligence intentionally omit `defaultOpen` so a fresh
    // sidebar (no persisted open-state) only shows Monitoring expanded. This
    // keeps the first-load surface area focused on the most-trafficked group
    // (Dashboard / Sites) while still letting users open the others —
    // their preference then persists via `persistOpenState`.
    const assets: NavGroup = {
      key: 'assets',
      title: t('assets'),
      icon: <WidgetsOutlined />,
      // Priority-ordered top entries per product direction (Chargers, Vehicles, Batteries),
      // then everything else alphabetized.
      items: [
        { title: t('chargers'), path: chargers.root },
        { title: t('vehicles'), path: vehicles.root },
        ...(hasBatteries ? [{ title: t('batteries'), path: batteries.root }] : []),
        ...(hasMeters ? [{ title: t('meters'), path: meters.root }] : []),
        ...sortByTitle(
          compact<NavLink>([
            { title: t('generators'), path: settings.generators.root },
            hasFuelTanks && { title: t('fuelTanks'), path: settings.fuelTanks.root },
            hasGridConnections && { title: t('gridConnections'), path: settings.gridConnections.root },
            hasLoads && { title: t('loads'), path: settings.loads.root },
            hasPanels && { title: t('panels'), path: settings.panels.root },
            hasSolarArrays && { title: t('solarArrays'), path: settings.solarArrays.root },
            { title: t('synopLinks'), path: settings.links.root }
          ]),
          locale
        )
      ]
    }

    const intelligence: NavGroup = {
      key: 'intelligence',
      title: t('intelligence'),
      icon: <InsightsOutlined />,
      items: compact<NavLink>([
        hasSynopAutopilot && {
          title: t('chargeAi'),
          path: chargeai.root
        },
        !readOnly &&
          generativeDashboardEnabled && {
            title: t('generativeDashboard'),
            path: generativeDashboard.root,
            badgeContent: t('alpha'),
            badgeColor: 'purple'
          },
        !readOnly &&
          aiAssistant && {
            title: t('assistantSkills'),
            path: settings.assistantSkills.root
          },
        // Reports trigger server-side generation, so read-only users never get
        // the hub or the builder — only the read-only Energy/Vehicle summaries.
        ...(reportHub && !readOnly
          ? [{ title: t('reports'), path: reports.root }]
          : [
              !readOnly && { title: t('reportsCustom'), path: reports.root, exactMatch: true },
              { title: t('reportsEnergy'), path: reports.energy },
              { title: t('reportsVehicles'), path: reports.vehicles }
            ])
      ])
    }

    const commercePriorityItems = compact<NavLink>([
      !readOnly && hasCostExplorer && { title: t('overview'), path: settings.billingOverview, ...adminOnly },
      showProductsAndAgreements && { title: t('products'), path: settings.products.root, ...adminOnly },
      showProductsAndAgreements && { title: t('agreements'), path: settings.agreements.root, ...adminOnly },
      !readOnly &&
        hasInvoiceTemplates &&
        showFleetBilling && {
          title: t('invoiceTemplates'),
          path: settings.invoiceTemplates.root,
          ...adminOnly
        },
      !readOnly &&
        hasTaxManagement &&
        showFleetBilling && {
          title: t('taxes'),
          path: settings.taxes.root,
          ...adminOnly
        },
      hasIntegrations && { title: t('paymentTerminals'), path: settings.paymentTerminals }
    ])

    // Every item here is billing-backed, so it is hidden entirely for read-only users.
    const commerceOtherItems = readOnly
      ? []
      : compact<NavLink>([
          assistantUsage && { title: t('assistantUsage'), path: settings.assistantUsage, ...adminOnly },
          hasCostExplorer && { title: t('costExplorer'), path: settings.costExplorer, ...adminOnly },
          showFleetBilling && { title: t('billingHub'), path: settings.billingHub, ...adminOnly },
          showHomeCharging && { title: t('homeCharging'), path: settings.homeCharging.root, ...adminOnly },
          showPublicCharging && { title: t('publicCharging'), path: settings.publicCharging, ...adminOnly },
          showWorkplaceCharging && { title: t('workplaceCharging'), path: settings.workplaceCharging, ...adminOnly }
        ])

    const commerce: NavGroup = {
      key: 'commerce',
      title: t('commerceAndBilling'),
      icon: <AttachMoneyOutlined />,
      items: [...commercePriorityItems, ...sortByTitle(commerceOtherItems, locale)]
    }

    const more: NavGroup = {
      key: 'more',
      title: t('more'),
      icon: <MoreHorizOutlined />,
      items: sortByTitle(
        compact<NavLink>([
          // Inside the desktop shell this page would only offer what is
          // already running.
          hasDesktopApp &&
            !isDesktopShell && {
              title: t('desktopApp'),
              path: settings.desktopApp,
              badgeContent: t('alpha'),
              badgeColor: 'purple'
            },
          { title: t('fleets'), path: settings.fleets.root },
          { title: t('locationsOfInterest'), path: settings.locations },
          { title: t('tariffs'), path: settings.tariffs.root }
        ]),
        locale
      )
    }

    const administration: NavGroup = {
      key: 'administration',
      title: t('administration'),
      icon: <AdminPanelSettingsOutlined />,
      action: 'read',
      subject: 'admin_settings',
      items: sortByTitle(
        compact<NavLink>([
          hasAssetSharing && {
            title: t('assetSharing'),
            path: settings.assetSharing.root,
            badgeContent: t('beta'),
            badgeColor: 'purple',
            ...adminOnly
          },
          { title: t('authSettings'), path: settings.auth, ...adminOnly },
          // Platform-internal firmware catalog: Synop staff + advanced-firmware gate
          isSynopInternal &&
            hasAdvancedFirmware && { title: t('firmwareCatalog'), path: settings.firmwareCatalog, ...adminOnly },
          // Email intake lives under Integrations. It needs operations-ui and hasOperations,
          // and orgs may have that pair without the integrations catalog gate.
          (hasIntegrations || (operationsSettings && hasOperations)) && {
            title: t('integrations'),
            path: settings.integrations.root,
            ...adminOnly
          },
          { title: t('organizations'), path: settings.orgs, ...adminOnly },
          { title: t('organizationSettings'), path: settings.orgManagement, ...adminOnly },
          canEditTheme && { title: t('theme'), path: settings.themeEditor, ...adminOnly },
          { title: t('userGroups'), path: settings.userGroups.root, ...adminOnly },
          { title: t('users'), path: settings.users.root, action: 'read', subject: 'user_settings' },
          hasWorkflows && {
            title: t('workflows'),
            path: workflows.root,
            ...adminOnly
          }
        ]),
        locale
      )
    }

    const operationsGroup: NavGroup = {
      key: 'operations',
      title: t('operations'),
      icon: <MonitorHeartOutlined />,
      items: [
        { title: t('operationsTickets'), path: operations.issues, alsoActiveOn: isOperationsTicketDetailPath },
        { title: t('operationsPlans'), path: operations.plans, alsoActiveOn: isOperationsPlanDetailPath },
        {
          title: t('operationsProjects'),
          path: operations.root,
          exactMatch: true,
          alsoActiveOn: isOperationsProjectDetailPath
        }
      ]
    }

    const customerAssetsByUrl = getCustomerAssets()
    const developer: NavGroup = {
      key: 'developer',
      title: t('developer'),
      icon: <CodeOutlined />,
      items: sortByTitle(
        compact<NavLink>([
          { title: t('apiKeys'), path: settings.apiKeys },
          !customerAssetsByUrl?.supportEmail && {
            title: t('documentation'),
            path: 'https://docs.synop.ai',
            openInNewTab: true
          }
        ]),
        locale
      )
    }

    // `more` sits at the very bottom (after Developer) so the most-trafficked
    // groups stay close to the top and the catch-all bucket is a known
    // "everything else" landing pad below the rest.
    return compact<NavGroup>([
      monitoring,
      assets,
      hasOperations && operationsGroup,
      intelligence,
      commerce,
      administration,
      developer,
      more
    ])
  }, [
    t,
    locale,
    readOnly,
    isSynopInternal,
    hasAdvancedFirmware,
    hasBatteries,
    hasPanels,
    hasFuelTanks,
    hasGridConnections,
    hasLoads,
    hasMeters,
    hasSolarArrays,
    hasWorkflows,
    hasOperations,
    operationsSettings,
    hasIntegrations,
    hasCostExplorer,
    hasSynopAutopilot,
    showProductsAndAgreements,
    showHomeCharging,
    showWorkplaceCharging,
    showPublicCharging,
    showFleetBilling,
    canEditTheme,
    hasInvoiceTemplates,
    hasTaxManagement,
    hasAssetSharing,
    aiAssistant,
    assistantUsage,
    reportHub,
    generativeDashboardEnabled,
    hasDesktopApp,
    isDesktopShell
  ])
}
